import { ConfigService } from './config.service';
import { AppTrial } from '../models/app-trial.model';
import { Team } from '../models/team.model';
import { LoaderService } from './loader.service';
import { FirebaseService } from './firebase.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { OrderRequest } from '../models/order-request.model';
import { AuthService, User } from './auth.service';
import { TeamsService } from './teams.service';
import { Injectable, OnDestroy } from '@angular/core';
import { Platform, AlertController, ToastController } from '@ionic/angular';
import {
  InAppPurchase2,
  IAPProduct,
} from '@ionic-native/in-app-purchase-2/ngx';
import { BehaviorSubject, Subscription } from 'rxjs';
import moment from 'moment';
import { CrashService } from './crash.service';
import firebase from 'firebase/app';
import 'firebase/firestore';

export interface AppProduct {
  id: string;
  type: string;
}

@Injectable({
  providedIn: 'root',
})
export class IapService implements OnDestroy {
  iapSubscription: Subscription;
  userDataSubscription: Subscription;
  userData: User;

  private msgsSubject = new BehaviorSubject(null);
  msgs$ = this.msgsSubject.asObservable();
  msgs: any = [];
  subMsgs: Subscription;
  productToregister: AppProduct[];

  constructor(
    private crash: CrashService,
    public fbs: FirebaseService,
    private plt: Platform,
    private config: ConfigService,
    public toastController: ToastController,
    public translate: TranslateService,
    public alertController: AlertController,
    private store: InAppPurchase2,
    private ionLoader: LoaderService,
    public auth: AuthService,
    private teamsService: TeamsService
  ) {
    this.userDataSubscription = this.auth.userData$.subscribe(
      async (userData: User) => {
        console.log(`changed user data to`, userData);
        if (userData && userData.orderRequest) {
          console.log(`have an order status`, userData.orderRequest);
          this.userData = userData;
        } else {
          console.log('Have no purchase requests to process');
        }
      },
      (error: any) => {
        this.crash.subscription('iap-service', 'userDataSubscription', error);
      }
    );
  }

  ngOnDestroy() {
    if (this.userDataSubscription) {
      this.userDataSubscription.unsubscribe();
    }
  }

  msg(msg) {
    console.log(msg);
    this.msgs.push(msg);
    this.msgsSubject.next(msg);
  }

  initializeStore() {
    // After platform ready
    this.store.verbosity = this.store.DEBUG;

    this.store.validator =
      'https://validator.fovea.cc/v1/validate?appName=com.grahamsoftware.quicktextforteams&apiKey=4b45d697-1833-4343-8971-add71775beaf';

    this.productToregister = [
      {
        id: this.config.trialProduct,
        type: this.store.NON_RENEWING_SUBSCRIPTION,
      },
      {
        id: this.config.quarterlyProduct,
        type: this.store.NON_RENEWING_SUBSCRIPTION,
      },
      {
        id: this.config.annualProduct,
        type: this.store.NON_RENEWING_SUBSCRIPTION,
      },
    ];

    this.productToregister.forEach((product) => {
      // Register the product
      this.store.register(product);

      // Register event handlers for the specific product
      this.store
        .when(product.id)
        .registered((registeredProduct: IAPProduct) => {
          this.msg(`Registered: ${registeredProduct.id}`);
        });

      // Updated
      this.store.when(product.id).updated((updatedProduct: IAPProduct) => {
        console.log(
          `Updated: ${updatedProduct.id}`,
          JSON.stringify(updatedProduct)
        );
      });

      // User closed the native purchase dialog
      this.store.when(product.id).cancelled((cancelledProduct) => {
        this.msg(`purchase cancelled: ${cancelledProduct.id}`);
        this.ionLoader.hideLoader('purchase cancelled');
      });

      // Product purchase finished
      this.store.when(product.id).finished((cancelledProduct) => {
        this.msg(`Finished: ${cancelledProduct.id}`);
      });

      // User already owns the product
      this.store.when(product.id).owned((ownedProduct) => {
        console.log(`Owned: ${ownedProduct.id}`, JSON.stringify(ownedProduct));
      });

      try {
        this.store
          .when(product.id)
          .approved(async (approvedProduct) => {
            console.log(
              `Product approved ${approvedProduct.id}`,
              JSON.stringify(approvedProduct)
            );
            // alert(`Product approved ${approvedProduct.id}`);
            // alert(JSON.stringify(approvedProduct));
            // approvedProduct.verify(); // THIS SEEMS TO BE THE RIGHT WAY

            // BUT THIS IS WHAT WORKS. MIGHT BE A PROD VERSUS SANDBOX VERIFICATION URL ISSUE
            await this.purchaseVerified(product.id, approvedProduct);
          })
          .verified((verifiedProduct) => {
            console.log(
              `Product verified ${verifiedProduct.id}`,
              JSON.stringify(verifiedProduct)
            );
            this.purchaseVerified(product.id, verifiedProduct);
          });
      } catch (error) {
        alert(error);
      }
    });

    // Track all store errors
    this.store.error((err) => {
      this.msg('Store Error ' + JSON.stringify(err));
      let msg = err.message;
      this.crash.iap('iap-service', 'store.error', msg, { err });
      switch (err.code) {
        case '6777010': // Could not connect to store
          msg = `${err.message}. Please try again later.`;
          this.purchasingError(msg);
          this.ionLoader.hideLoader('store error 6777010');
          break;
        case '6778001':
          // Transaction not on apple server (bad data from testing?)
          msg = `${err.message}. Transaction not on apple server.`;
          this.purchasingError(msg);
          console.log('store error 6778001 need to hide loader?');
          break;
        case '6777017':
          // Transaction verification failed: Transaction with given ID isn't registered with the Apple server.
          msg = `${err.message}. Transaction is not registered.`;
          this.purchasingError(msg);
          console.log('store error 6777017 need to hide loader?');
          break;
        default:
          this.purchasingError(msg);
          this.ionLoader.hideLoader('unknown purchasing error');
      }
    });

    // Run some code only when the store is ready to be used
    this.store.ready(() => {
      this.msg('Store is ready');
      this.msg(`${this.store.products.length} Products registered`);
    });

    // Refresh the status of in-app products
    this.store.refresh();
  }

  async purchasingError(errMsg: string) {
    const toast = await this.toastController.create({
      message: this.translate.instant(errMsg),
      duration: 5000,
      color: 'danger',
      // JL_TODO: add support link here for fatal errors
      // buttons: [
      //   {
      //     text: 'Close',
      //     role: 'cancel',
      //     handler: () => {
      //       console.log('Cancel clicked');
      //     },
      //   },
      // ],
    });
    toast.present();
  }

  async purchaseVerified(productId, p = null) {
    console.log(`Purchase verified for product ${productId}`);

    const user = await this.fbs.afAuth.currentUser;
    if (user) {
      this.iapSubscription = this.fbs
        .iapsWithIds$(`iaps`, (ref) =>
          ref
            .where('iapUid', '==', user.uid)
            .where('iapProduct', '==', productId)
            .where('iapStatus', '==', 'requested')
        )
        .subscribe(
          async (iaps) => {
            if (iaps.length > 0) {
              console.log(`Found open purchases for '${productId}'`, iaps);

              if (iaps.length > 1) {
                this.crash.iap(
                  'iap-service',
                  'purchaseVerified',
                  'Found more than one open purchase',
                  { productId }
                );
                console.log(
                  `Found more than one open purchase for '${productId}'`,
                  iaps
                );
              }

              const orderRequest = iaps[0];
              this.iapSubscription.unsubscribe(); // JL_TODO: Recall why this is here and verify needed
              await this.iapUpdate(orderRequest.iapId, {
                iapStatus: 'verified',
              });
              await this.savePurchase(orderRequest);
              await this.iapUpdate(orderRequest.iapId, {
                iapStatus: 'completed',
              });

              // Real transaction?
              if (!this.config.mock) {
                p.finish();
              }
              this.ionLoader.hideLoader('purchaseVerified, success');
              this.alertPurchase(
                orderRequest.iapProduct,
                orderRequest.teamName
              );
            } else {
              this.crash.iap(
                'iap-service',
                'purchaseVerified',
                'No open purchases',
                { productId }
              );
              this.ionLoader.hideLoader('no open purchases');
            }
          },
          (error: any) => {
            this.crash.subscription('iap-service', 'iapSubscription', error);
            this.ionLoader.hideLoader('iap subscription error');
          }
        );
    } else {
      this.crash.iap(
        'iap-service',
        'purchaseVerified',
        'Cannot find user to purchase',
        {
          productId,
        }
      );
      this.ionLoader.hideLoader('purchasing user not found');
    }
  }

  isMobileApp() {
    if (!this.plt.is('cordova')) {
      return false;
    }
    if (this.plt.is('desktop')) {
      return false;
    }

    if (this.plt.is('ios')) {
      if (this.plt.is('mobileweb')) {
        return false;
      } else {
        return true;
      }
    }

    if (this.plt.is('android')) {
      if (this.plt.is('mobileweb')) {
        return false;
      } else {
        return true;
      }
    }

    return false;
  }

  isIos() {
    if (!this.plt.is('cordova')) {
      return false;
    }
    if (this.plt.is('desktop')) {
      return false;
    }
    if (this.plt.is('android')) {
      return false;
    }

    if (this.plt.is('ios')) {
      if (this.plt.is('mobileweb')) {
        return false;
      } else {
        return true;
      }
    }

    return false;
  }

  isAndroid() {
    if (!this.plt.is('cordova')) {
      return false;
    }
    if (this.plt.is('desktop')) {
      return false;
    }
    if (this.plt.is('ios')) {
      return false;
    }

    if (this.plt.is('android')) {
      if (this.plt.is('mobileweb')) {
        return false;
      } else {
        return true;
      }
    }

    return false;
  }

  // TEST FUNCTIONS TO TRIGGER USER/TEAM STATUS CHANGES

  userNeverHadTrial(teamId) {
    this.auth.updateUser({ trial: { status: 'none', expires: null } });
    this.teamsService.teamUpdate(teamId, {
      teamPlan: { product: 'none', expired: false, expires: null },
    });
  }

  userTrialActiveNoTeamPlan(teamId) {
    this.auth.updateUser({ trial: { status: 'active', expires: null } });
    this.teamsService.teamUpdate(teamId, {
      teamPlan: { product: 'none', expired: false, expires: null },
    });
  }

  userTrialActiveTrialTeamPlan(teamId) {
    this.auth.updateUser({ trial: { status: 'active', expires: null } });
    this.teamsService.teamUpdate(teamId, {
      teamPlan: {
        product: this.config.trialProduct,
        expired: false,
        expires: null,
      },
    });
  }

  userTrialActiveActiveTeamPlan(teamId) {
    this.auth.updateUser({ trial: { status: 'active', expires: null } });
    this.teamsService.teamUpdate(teamId, {
      teamPlan: {
        product: this.config.annualProduct,
        expired: false,
        expires: null,
      },
    });
  }

  userTrialActiveExpiredTeamPlan(teamId) {
    this.auth.updateUser({ trial: { status: 'active', expires: null } });
    this.teamsService.teamUpdate(teamId, {
      teamPlan: {
        product: this.config.annualProduct,
        expired: true,
        expires: null,
      },
    });
  }

  userTrialExpiredNoTeamPlan(teamId) {
    this.auth.updateUser({ trial: { status: 'expired', expires: null } });
    this.teamsService.teamUpdate(teamId, {
      teamPlan: { product: 'none', expired: false, expires: null },
    });
  }

  userTrialExpiredTrialTeamPlan(teamId) {
    this.auth.updateUser({ trial: { status: 'expired', expires: null } });
    this.teamsService.teamUpdate(teamId, {
      teamPlan: {
        product: this.config.trialProduct,
        expired: false,
        expires: null,
      },
    });
  }

  userTrialExpiredActiveTeamPlan(teamId) {
    this.auth.updateUser({ trial: { status: 'expired', expires: null } });
    this.teamsService.teamUpdate(teamId, {
      teamPlan: {
        product: this.config.annualProduct,
        expired: false,
        expires: null,
      },
    });
  }

  userTrialExpiredExpiredTeamPlan(teamId) {
    this.auth.updateUser({ trial: { status: 'expired', expires: null } });
    this.teamsService.teamUpdate(teamId, {
      teamPlan: {
        product: this.config.annualProduct,
        expired: true,
        expires: null,
      },
    });
  }

  async order(productId: string) {
    if (this.config.mock) {
      console.log(`Pretend to purchase '${productId}'`);
      await this.purchaseVerified(productId);
    } else {
      console.log(`start the purchase on the store '${productId}'`);
      this.store
        .order(productId)
        .then((response) => {
          console.log('order response', response);
        })
        .error((err) => console.log('order error', err));
    }
  }

  async requestBuyStartTrial(team: Team, trialStatus: AppTrial) {
    const iapMsg = `User never had trial, give team trial plan`;
    console.log(iapMsg);
    const orderRequest = {
      iapProduct: this.config.trialProduct,
      iapState: 'startTrial',
      iapStatus: 'requested',
      iapMsg,
      teamId: team.teamId,
      teamName: team.teamName,
      trialStatusBefore: trialStatus,
      teamPlanBefore: team.teamPlan,
      teamPlanAfter: {
        product: this.config.trialProduct,
        expires: null,
        expired: false,
      },
      trialStatusAfter: {
        status: 'active',
        expires: null,
      },
    };
    await this.request(orderRequest);
  }

  async requestBuyWithActiveTrial(
    team: Team,
    trialStatus: AppTrial,
    iapProduct
  ) {
    const iapMsg = `User trial is currently active but not on this team, offer buy`;
    console.log(iapMsg);
    const orderRequest = {
      iapProduct,
      iapState: 'buyWithActiveTrial',
      iapStatus: 'requested',
      iapMsg,
      teamId: team.teamId,
      teamName: team.teamName,
      trialStatusBefore: trialStatus,
      teamPlanBefore: team.teamPlan,
      teamPlanAfter: {
        product: iapProduct,
        expires: null,
        expired: false,
      },
      trialStatusAfter: trialStatus,
    };
    await this.request(orderRequest);
  }

  async requestRenewWithActiveTrial(
    team: Team,
    trialStatus: AppTrial,
    iapProduct
  ) {
    const iapMsg = `User trial is active and this team has expired, offer renew`;
    console.log(iapMsg);
    const orderRequest = {
      iapProduct,
      iapState: 'renewWithActiveTrial',
      iapStatus: 'requested',
      iapMsg,
      teamId: team.teamId,
      teamName: team.teamName,
      trialStatusBefore: trialStatus,
      teamPlanBefore: team.teamPlan,
      teamPlanAfter: {
        product: iapProduct,
        expires: null,
        expired: false,
      },
      trialStatusAfter: trialStatus,
    };
    await this.request(orderRequest);
  }

  async requestBuyWithExpiredTrial(
    team: Team,
    trialStatus: AppTrial,
    iapProduct
  ) {
    const iapMsg = `User trial is expired and this team has no plan, offer buy`;
    console.log(iapMsg);
    const orderRequest = {
      iapProduct,
      iapState: 'buyWithExpiredTrial',
      iapStatus: 'requested',
      iapMsg,
      teamId: team.teamId,
      teamName: team.teamName,
      trialStatusBefore: trialStatus,
      teamPlanBefore: team.teamPlan,
      teamPlanAfter: {
        product: iapProduct,
        expires: null,
        expired: false,
      },
      trialStatusAfter: trialStatus,
    };
    await this.request(orderRequest);
  }

  async requestRenewWithExpiredTrial(
    team: Team,
    trialStatus: AppTrial,
    iapProduct
  ) {
    const iapMsg = `User trial is expire and this team has expired, offer renew`;
    console.log(iapMsg);
    const orderRequest = {
      iapProduct,
      iapState: 'renewWithExpiredTrial',
      iapStatus: 'requested',
      iapMsg,
      teamId: team.teamId,
      teamName: team.teamName,
      trialStatusBefore: trialStatus,
      teamPlanBefore: team.teamPlan,
      teamPlanAfter: {
        product: iapProduct,
        expires: null,
        expired: false,
      },
      trialStatusAfter: trialStatus,
    };
    await this.request(orderRequest);
  }

  async request(orderRequest: OrderRequest) {
    console.log(`iap request`, orderRequest);
    this.ionLoader.showLoader('iap request');
    try {
      this.msg(orderRequest.iapMsg);
      await this.iapAdd(orderRequest);
      if (orderRequest.iapStatus !== 'invalid') {
        this.order(orderRequest.iapProduct);
      } else {
        this.crash.iap('iap-service', 'request', 'Invalid purchase attempt', {
          orderRequest,
        });
        this.ionLoader.hideLoader('invalid purchase attempt');
      }
    } catch (error) {
      console.log('purchase request error', error);
      this.crash.iap('iap-service', 'request', 'Purchase request error', {
        orderRequest,
        error,
      });
      this.ionLoader.hideLoader('purchase request error');
    }
  }

  async iapAdd(data): Promise<firebase.firestore.DocumentReference> {
    const user = await this.fbs.afAuth.currentUser;
    if (user) {
      data.iapUid = user.uid;
    } else {
      data.uid = 'unknown';
    }
    console.log('iapAdd()', data);
    data.requestedAt = this.fbs.timestamp;
    return this.fbs.collection(`/iaps`).add(data);
  }

  async iapUpdate(iapId, changes) {
    this.fbs.update(`/iaps/${iapId}`, changes);
  }

  async savePurchase(orderRequest: OrderRequest) {
    console.log('savePurchase');

    const now = moment().utc().add(1, 'day');

    // EXPIRATION
    if (this.config.debugExpiration) {
      orderRequest.teamPlanAfter.expires = now.add(30, 'minutes').format('LLL');
      if (
        orderRequest.teamPlanAfter.product === this.config.trialProduct ||
        orderRequest.teamPlanAfter.product === this.config.quarterlyProduct
      ) {
        orderRequest.teamPlanAfter.expires = now
          .add(10, 'minutes')
          .format('LLL');
      }
    } else {
      switch (orderRequest.teamPlanAfter.product) {
        case this.config.trialProduct:
          orderRequest.teamPlanAfter.expires = now.add(30, 'day').format('LL');
          break;
        case this.config.quarterlyProduct:
          orderRequest.teamPlanAfter.expires = now.add(3, 'month').format('LL');
          break;
        case this.config.annualProduct:
          orderRequest.teamPlanAfter.expires = now.add(1, 'year').format('LL');
          break;
      }
    }

    await this.teamsService.teamUpdate(orderRequest.teamId, {
      teamPlan: orderRequest.teamPlanAfter,
    });
    if (orderRequest.trialStatusAfter !== null) {
      await this.auth.updateUser({
        trial: orderRequest.trialStatusAfter,
      });
    }
  }

  async alertPurchase(productId, teamName) {
    let productCaption = 'Your free 30 Day Trial has started.';
    let header = this.translate.instant(
      'IAP.DIALOGS.PURCHASED.TITLE_PURCHASED'
    );
    switch (productId) {
      case this.config.annualProduct:
        productCaption = 'Your 1 Year Premium upgrade was successful.';
        break;
      case this.config.quarterlyProduct:
        productCaption = 'Your 3 Month Premium upgrade was successful.';
        break;
      default:
        header = this.translate.instant('IAP.DIALOGS.PURCHASED.TITLE_TRIAL');
    }
    const message = `${productCaption} Team '${teamName}' may send new broadcasts and replies until expiration.`;
    const alert = await this.alertController.create({
      header,
      message,
      buttons: [
        {
          text: this.translate.instant(
            'IAP.DIALOGS.PURCHASED.BUTTONS.CONTINUE'
          ),
          handler: () => {
            console.log('Purchase acknowledged');
          },
        },
      ],
    });

    await alert.present();
  }

  /*
  Purchasing use cases:

    x no trial yet
    x trial active
      x trial on team
      x no plan on team
      x plan on team
      x expired plan on team
    x trial expired
      x trial on team
      x no plan on team
      x plan on team
      x expired plan on team

  */
  hasValidPlan(isUserAdmin, trial, team) {
    // If the the user is the admin of the team, check their own status
    if (isUserAdmin) {
      console.log(`User IS the team admin`);
      switch (trial.status) {
        case 'none':
          console.log(`User never had trial`);
          // Just purchased one?
          if (team.teamPlan.product === this.config.trialProduct) {
            return true;
          } else {
            console.log(`User never had trial, probably promoted to admin`);
            if (team.teamPlan.expired) {
              console.log(`User never had trial, but expired team plan`);
              return false;
            }
            if (team.teamPlan.product === 'none') {
              console.log(`User never had trial, but team has no plan`);
              return false;
            }
            console.log(`User never had trial, but team plan is valid`);
            return true;
          }
        case 'active':
          switch (team.teamPlan.product) {
            case 'none':
              console.log(`User trial active, no team plan`);
              return false;
            case team.teamPlan.product:
              console.log(`User trial active, trial team plan`);
              break;
            case this.config.quarterlyProduct:
            case this.config.annualProduct:
              if (team.teamPlan.expired) {
                console.log(`User trial active, expired team plan`);
                return false;
              } else {
                console.log(`User trial active, active team plan`);
              }
              break;
          }
          break;
        case 'expired':
          switch (team.teamPlan.product) {
            case 'none':
              console.log(`User trial expired, no team plan`);
              return false;
            case this.config.trialProduct:
              console.log(`User trial expired, trial team plan`);
              return false;
            case this.config.quarterlyProduct:
            case this.config.annualProduct:
              if (team.teamPlan.expired) {
                console.log(`User trial expired, expired team plan`);
                return false;
              } else {
                console.log(`User trial expired, active team plan`);
              }
          }
          break;
        default:
          console.log(`Invalid trial status '${trial.status}'`);
          return false;
      }

      // Team plan has expired?
      if (team.teamPlan.expired) {
        console.log(`Team plan has expired`);
        return false;
      } else {
        console.log(`Team plan has NOT expired, allow continue`);
        return true;
      }
    } else {
      console.log(`User is NOT the team admin`);
      // If the the user is not the admin of the team, check the team plan
      switch (team.teamPlan.product) {
        case 'none':
          console.log(`User trial active, no team plan`);
          return false;
        case this.config.trialProduct:
          console.log(`User trial active, trial team plan`);
          break;
        case this.config.quarterlyProduct:
        case this.config.annualProduct:
          if (team.teamPlan.expired) {
            console.log(`User trial active, expired team plan`);
            return false;
          } else {
            console.log(`User trial active, active team plan`);
          }
          break;
      }

      // Team plan has expired?
      if (team.teamPlan.expired) {
        console.log(`Team plan has expired`);
        return false;
      } else {
        console.log(`Team plan has NOT expired, allow continue`);
        return true;
      }
    }
  }
}
